/**
 * web/src/components/access/UserList.tsx
 *
 * Admin User Access — flat user roster table. Replaced the previous
 * users × (event × workflow type) grid because the cell grants were
 * advisory metadata only (nothing in the workflow engine or API layer
 * read them). The roster keeps the surfaces that ARE wired end-to-end:
 *
 *   - Role dropdown   → PATCH /api/admin/users/:id/role (server enforces
 *                       the enum and the no-self-demote-from-admin guard).
 *   - Slack User ID   → PATCH /api/admin/users/:id/slack-user-id (used by
 *                       the 48h follow-up scheduler to <@mention> the
 *                       original approver).
 *   - Delete user     → DELETE /api/admin/users/:id (cascades Session +
 *                       UserEventAccess; preserves forensic refs).
 *
 * Self-protection: the row for the currently-logged-in admin disables the
 * "demote away from admin" options and hides the delete affordance, in
 * sync with the server-side guards. Mismatch would still produce a clear
 * 400 INVALID_OPERATION; we just save the round-trip.
 */
import { useEffect, useMemo, useState } from 'react';
import { useUpdateRole } from '../../hooks/mutations/useUpdateRole';
import { useUpdateSlackUserId } from '../../hooks/mutations/useUpdateSlackUserId';
import { useDeleteUser } from '../../hooks/mutations/useDeleteUser';
import { useToggleAccess } from '../../hooks/mutations/useToggleAccess';
import { useAuth } from '../../hooks/useAuth';
import { Input } from '../shared/Input';
import { Select } from '../shared/Select';
import { Button } from '../shared/Button';
import { Icon } from '../../lib/icons';
import { cn } from '../../lib/cn';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/dialog';
import type { AdminAccessEvent, AdminEventGrant, AdminUser } from '../../api/types';

/** Roles exposed in the dropdown. Order matches the CLAUDE.md User Roles table. */
const ROLE_OPTIONS = [
  { value: 'sales_ops', label: 'Sales ops' },
  { value: 'content', label: 'Content' },
  { value: 'operations', label: 'Operations' },
  { value: 'admin', label: 'Admin' },
] as const;

/** Public props for UserList. */
export interface UserListProps {
  users: AdminUser[];
  /** All events (full universe for the per-user access editor). */
  events: AdminAccessEvent[];
  /** Sparse (userId, eventId) grants — presence = access. */
  eventGrants: AdminEventGrant[];
}

/**
 * Renders the admin user roster table. Each row exposes the three wired
 * mutations (role, Slack ID, delete). Empty state matches the prior matrix
 * voice so an admin filtering down to zero rows sees consistent copy.
 *
 * @sideEffects subscribes to useDeleteUser; row-level subscribes to
 *   useUpdateRole + useUpdateSlackUserId via child rows
 * @idempotent yes — pure rendering modulo the mutations the children own
 */
export function UserList({ users, events, eventGrants }: UserListProps) {
  const { me } = useAuth();
  const deleteUser = useDeleteUser();
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null);
  const [pendingAccess, setPendingAccess] = useState<AdminUser | null>(null);

  // userId -> set of granted eventIds, recomputed as the cache updates.
  const grantsByUser = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const g of eventGrants) {
      const set = map.get(g.userId) ?? new Set<string>();
      set.add(g.eventId);
      map.set(g.userId, set);
    }
    return map;
  }, [eventGrants]);

  return (
    <div className="border border-border rounded-xl bg-bg overflow-hidden">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-bg-soft">
            <th
              scope="col"
              className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-subtle border-b border-border"
            >
              User{' '}
              <span className="font-normal text-text-faint normal-case tracking-normal">
                ({users.length})
              </span>
            </th>
            <th
              scope="col"
              className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-subtle border-b border-border w-[170px]"
            >
              Role
            </th>
            <th
              scope="col"
              className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-subtle border-b border-border w-[200px]"
            >
              Slack User ID
            </th>
            <th
              scope="col"
              className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-subtle border-b border-border w-[130px]"
            >
              Events
            </th>
            <th
              scope="col"
              className="text-right px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-subtle border-b border-border w-[60px]"
            >
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {users.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center text-[13px] text-text-subtle py-10">
                No users match this search.
              </td>
            </tr>
          )}
          {users.map((u) => (
            <UserRow
              key={u.id}
              user={u}
              isSelf={me?.id === u.id}
              grantedCount={(grantsByUser.get(u.id) ?? EMPTY_SET).size}
              totalEvents={events.length}
              onRequestDelete={() => setPendingDelete(u)}
              onManageAccess={() => setPendingAccess(u)}
            />
          ))}
        </tbody>
      </table>
      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this user?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="text-[13px] text-text leading-[1.6]">
              <p>
                <strong className="font-semibold">{pendingDelete?.name}</strong>{' '}
                <span className="text-text-muted">({pendingDelete?.email})</span> will be
                permanently removed. Their active session is invalidated immediately and every
                access grant for this user is dropped.
              </p>
              <p className="mt-3 text-text-muted">
                Historical workflow approvals, sent emails, and audit entries will still display
                this user's name for forensic context, but they will no longer be able to sign in.
                This action cannot be undone.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="default"
              onClick={() => setPendingDelete(null)}
              disabled={deleteUser.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={deleteUser.isPending || pendingDelete === null}
              onClick={() => {
                if (!pendingDelete) return;
                deleteUser.mutate(pendingDelete.id, {
                  onSettled: () => setPendingDelete(null),
                });
              }}
            >
              {deleteUser.isPending ? 'Deleting…' : 'Delete user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EventAccessDialog
        user={pendingAccess}
        events={events}
        grantedEventIds={pendingAccess ? (grantsByUser.get(pendingAccess.id) ?? EMPTY_SET) : EMPTY_SET}
        onClose={() => setPendingAccess(null)}
      />
    </div>
  );
}

/** Stable empty set so memoized lookups don't allocate per render. */
const EMPTY_SET: ReadonlySet<string> = new Set<string>();

/* -------------------------------------------------------------------- */
/* Per-row implementation                                                 */
/* -------------------------------------------------------------------- */

interface UserRowProps {
  user: AdminUser;
  isSelf: boolean;
  grantedCount: number;
  totalEvents: number;
  onRequestDelete: () => void;
  onManageAccess: () => void;
}
function UserRow({
  user,
  isSelf,
  grantedCount,
  totalEvents,
  onRequestDelete,
  onManageAccess,
}: UserRowProps) {
  const role = useUpdateRole();
  const slack = useUpdateSlackUserId();
  const isAdmin = user.role === 'admin';

  const initials = (user.name ?? '?')
    .split(/\s+/)
    .map((p) => p[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Slack User ID input is uncontrolled-from-server: local state mirrors
  // the server value, re-syncs on prop change, and commits on blur / Enter.
  const [slackValue, setSlackValue] = useState(user.slackUserId ?? '');
  useEffect(() => {
    setSlackValue(user.slackUserId ?? '');
  }, [user.slackUserId]);
  const commitSlack = () => {
    const trimmed = slackValue.trim();
    const next = trimmed === '' ? null : trimmed;
    if (next === user.slackUserId) return;
    slack.mutate({ userId: user.id, slackUserId: next });
  };

  return (
    <tr className="group">
      <td className="px-4 py-2.5 border-b border-border align-middle">
        <div className="flex items-center gap-2.5 min-w-[240px]">
          <div
            className={cn(
              'w-[26px] h-[26px] rounded-full bg-bg-muted text-text-muted shrink-0',
              'grid place-items-center text-[10.5px] font-semibold tracking-[0.02em]',
            )}
            aria-hidden="true"
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-text truncate">{user.name}</div>
            <div className="text-[11.5px] text-text-subtle truncate">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5 border-b border-border align-middle">
        <Select
          aria-label={`Role for ${user.name}`}
          value={user.role}
          disabled={role.isPending}
          onChange={(e) => {
            const next = e.target.value as 'sales_ops' | 'content' | 'operations' | 'admin';
            if (next === user.role) return;
            if (isSelf && next !== 'admin') {
              // Belt-and-braces UX guard — the dropdown disables these
              // options for the self row, but Safari sometimes restores
              // a disabled <option> selection on form reset. Reject here
              // so we never even hit the server.
              return;
            }
            role.mutate({ userId: user.id, role: next });
          }}
        >
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={isSelf && opt.value !== 'admin'}>
              {opt.label}
            </option>
          ))}
        </Select>
      </td>
      <td className="px-4 py-2.5 border-b border-border align-middle">
        <Input
          type="text"
          value={slackValue}
          onChange={(e) => setSlackValue(e.target.value)}
          onBlur={commitSlack}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          placeholder="U02ABCXYZ12"
          aria-label={`Slack User ID for ${user.name}`}
          className={cn('font-mono text-[12px]', slack.isPending && 'opacity-60')}
        />
      </td>
      <td className="px-4 py-2.5 border-b border-border align-middle">
        {isAdmin ? (
          <span className="text-[12px] text-text-subtle" title="Admins access every event">
            All events
          </span>
        ) : (
          <button
            type="button"
            onClick={onManageAccess}
            className={cn(
              'text-[12px] text-accent-text hover:underline',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-1 rounded',
            )}
            aria-label={`Manage event access for ${user.name}`}
          >
            {grantedCount} of {totalEvents}
          </button>
        )}
      </td>
      <td className="px-2 py-2.5 border-b border-border text-right align-middle">
        {!isSelf && (
          <button
            type="button"
            onClick={onRequestDelete}
            aria-label={`Delete ${user.name}`}
            title={`Delete ${user.name}`}
            className={cn(
              'w-7 h-7 rounded-md grid place-items-center inline-grid',
              'text-text-faint hover:text-failed hover:bg-failed-soft',
              'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
              'transition-opacity duration-100',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-failed focus-visible:outline-offset-1',
            )}
          >
            <Icon name="trash" size={13} />
          </button>
        )}
      </td>
    </tr>
  );
}

/* -------------------------------------------------------------------- */
/* Per-user event-access editor dialog                                   */
/* -------------------------------------------------------------------- */

interface EventAccessDialogProps {
  user: AdminUser | null;
  events: AdminAccessEvent[];
  grantedEventIds: ReadonlySet<string>;
  onClose: () => void;
}

/**
 * Dialog listing every event as a toggle for one user. Each toggle PUTs
 * /api/admin/access optimistically (the grant flips immediately via the
 * matrix cache); the checkbox state is derived from the live `grantedEventIds`
 * set, which re-renders as the cache updates. Granting is coarse — a checked
 * event means the user can see + act on the whole event.
 */
function EventAccessDialog({ user, events, grantedEventIds, onClose }: EventAccessDialogProps) {
  const toggle = useToggleAccess();
  return (
    <Dialog
      open={user !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Event access — {user?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-[12.5px] text-text-muted leading-[1.6] mb-3">
            Check the events {user?.name?.split(' ')[0] ?? 'this user'} may see and act on.
            Unchecked events are hidden from them entirely.
          </p>
          {events.length === 0 ? (
            <div className="text-[13px] text-text-subtle py-4">No events to grant.</div>
          ) : (
            <ul className="flex flex-col gap-1">
              {events.map((e) => {
                const granted = grantedEventIds.has(e.id);
                return (
                  <li key={e.id}>
                    <label className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-bg-soft cursor-pointer">
                      <input
                        type="checkbox"
                        checked={granted}
                        disabled={toggle.isPending || !user}
                        onChange={() => {
                          if (!user) return;
                          toggle.mutate({ userId: user.id, eventId: e.id, granted: !granted });
                        }}
                        className="h-4 w-4 accent-accent"
                      />
                      <span className="text-[13px] text-text">{e.name}</span>
                      <span className="text-[11.5px] text-text-subtle font-mono">{e.code}</span>
                      {!e.isActive && (
                        <span className="text-[11px] text-text-faint">(archived)</span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="default" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
